import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '../i18n/useI18n';
import { projectApi, logApi } from '../api/client';

export default function AddLog() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pid = searchParams.get('pid');

  const [project, setProject] = useState(null);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    reason: '',
    change_value: '',
    comment: '',
    business_time: new Date().toISOString().slice(0, 16).replace('T', ' '),
  });
  const [showCustomReason, setShowCustomReason] = useState(false);
  const [customReason, setCustomReason] = useState('');

  useEffect(() => {
    if (!pid) {
      alert(t('paramError'));
      navigate('/');
      return;
    }
    initPage();
  }, [pid]);

  async function initPage() {
    try {
      // 获取工程信息
      const pRes = await projectApi.getList();
      if (pRes.success) {
        const proj = pRes.data.projects.find(p => p.project_id == pid);
        if (proj) {
          setProject(proj);
        }
      }

      // 获取预设
      const preRes = await projectApi.getPresets(pid);
      if (preRes.success) {
        setPresets(preRes.data.presets || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  function handleReasonChange(e) {
    const value = e.target.value;
    if (value === '__CUSTOM__') {
      setShowCustomReason(true);
      setFormData({ ...formData, reason: '', change_value: '' });
    } else {
      setShowCustomReason(false);
      const preset = presets.find(p => p.reason === value);
      if (preset) {
        setFormData({ ...formData, reason: value, change_value: preset.points.toString() });
      } else {
        setFormData({ ...formData, reason: value });
      }
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    let finalReason = formData.reason;
    if (showCustomReason) {
      finalReason = customReason.trim();
      if (!finalReason) {
        alert(t('enterCustomReasonName'));
        return;
      }
    }

    if (!finalReason) {
      alert(t('selectOrInputReason'));
      return;
    }

    try {
      const res = await logApi.create({
        project_id: pid,
        reason: finalReason,
        change_value: parseFloat(formData.change_value),
        comment: formData.comment,
        business_time: formData.business_time.replace('T', ' ') + ':00',
      });

      if (res.success) {
        alert(t('submitSuccess'));
        navigate(`/?pid=${pid}`);
      } else {
        alert(t('submitFailed') + (res.message || t('actionFailed')));
      }
    } catch (err) {
      alert(t('networkFailed'));
    }
  }

  if (loading) {
    return <div className="min-h-screen bg-gray-100 flex items-center justify-center"><div className="text-gray-400">{t('loading')}</div></div>;
  }

  if (!project) {
    return <div className="min-h-screen bg-gray-100 flex items-center justify-center"><div className="text-gray-400">{t('projectNotExist')}</div></div>;
  }

  const isNegative = project.type == 1;
  const headerBg = isNegative ? 'bg-red-600' : 'bg-green-600';
  const submitBg = isNegative ? 'bg-red-600 shadow-red-100' : 'bg-green-600 shadow-green-100';

  return (
    <div className="min-h-screen bg-gray-100 p-4 flex items-start justify-center">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
        <div className={`p-6 text-white ${headerBg} transition-all`}>
          <h3 className="text-xl font-bold flex items-center">
            <span className="mr-2">✍️</span> {t('applyTitle')}
          </h3>
          <p className="text-white/70 text-[10px] mt-1 uppercase tracking-widest font-bold">
            {isNegative ? t('negativeSystem') : t('positiveSystem')}：{project.title}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-xs font-bold text-gray-400 mb-2">{t('reason')}</label>
            <select
              value={showCustomReason ? '__CUSTOM__' : formData.reason}
              onChange={handleReasonChange}
              required
              className="w-full bg-gray-50 border-2 border-gray-50 rounded-xl p-3 outline-none focus:border-indigo-500 transition-all font-bold text-gray-700"
            >
              <option value="">{t('selectReason')}</option>
              {presets.map((p, idx) => (
                <option key={idx} value={p.reason}>
                  {p.reason} ({p.points > 0 ? '+' : ''}{p.points})
                </option>
              ))}
              <option value="__CUSTOM__">✨ {t('customReason')}</option>
            </select>
          </div>

          {showCustomReason && (
            <div className="animate-fadeIn">
              <label className="block text-xs font-bold text-indigo-500 mb-2">{t('enterCustomReason')}</label>
              <input
                type="text"
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder={t('customReasonPlaceholder')}
                className="w-full bg-indigo-50 border-2 border-indigo-100 rounded-xl p-3 outline-none focus:border-indigo-500 transition-all font-bold text-gray-800"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-bold text-gray-400 mb-2">{t('changeValue')}</label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                value={formData.change_value}
                onChange={(e) => setFormData({ ...formData, change_value: e.target.value })}
                required
                placeholder="0.00"
                className="w-full bg-gray-50 border-2 border-gray-50 rounded-xl p-3 pl-10 outline-none focus:border-indigo-500 transition-all font-mono font-bold text-lg"
              />
              <span className="absolute left-4 top-3.5 text-gray-300 font-bold">±</span>
            </div>
            <p className="text-[9px] text-gray-400 mt-2 px-1 italic">
              {isNegative ? t('negativeHint') : t('positiveHint')}
            </p>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-400 mb-2">{t('comment')}</label>
            <textarea
              value={formData.comment}
              onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
              rows="2"
              placeholder={t('commentPlaceholder')}
              className="w-full bg-gray-50 border-2 border-gray-50 rounded-xl p-3 outline-none focus:border-indigo-500 transition-all text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-400 mb-2">{t('businessTime')}</label>
            <input
              type="datetime-local"
              value={formData.business_time.replace(' ', 'T').slice(0, 16)}
              onChange={(e) => setFormData({ ...formData, business_time: e.target.value.replace('T', ' ') })}
              required
              className="w-full bg-gray-50 border-2 border-gray-50 rounded-xl p-3 outline-none focus:border-indigo-500 transition-all text-sm font-medium"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex-1 py-4 bg-gray-100 text-gray-400 rounded-2xl font-bold active:scale-95 transition-all"
            >
              {t('back')}
            </button>
            <button
              type="submit"
              className={`flex-[2] text-white py-4 rounded-2xl font-bold shadow-lg active:scale-95 transition-all ${submitBg}`}
            >
              {t('confirm')} {t('submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
